<script setup>
import {Disclosure, DisclosureButton, DisclosurePanel} from "@headlessui/vue";
import {useCouponStore} from "@stores/coupon";

const couponStore = useCouponStore();

const coupons = computed(() => {
  let couponsList = couponStore.coupons.filter(
      (coupon) => coupon.coupon.event === 1,
  );
  console.log(couponsList);
  return couponsList;
});

function showCouponAmount(coupon) {
  console.log(coupon);

  // fixed amount
  if (coupon.coupon.event === 2) {
    return money(coupon.coupon.amount);
  }

  // fixed amount
  if (
      coupon.coupon.event === 1 &&
      coupon?.coupon?.deposit?.deposit_type === 1
  ) {
    return money(coupon.coupon.amount / 100);
  }

  // percentage amount
  if (
      coupon.coupon.event === 1 &&
      coupon?.coupon?.deposit?.deposit_type === 2
  ) {
    return `${coupon.coupon.amount} %`;
  }
}
</script>

<template>
  <div>
    <div class="flex flex-row justify-between">
      <label class="inline-flex items-center gap-2 text-sm text-gray-400">
        <span
          class="inline-flex items-center justify-center rounded-full bg-brand-primary/20 p-2"
          :class="{ 'bg-green-500/20': couponStore.isCouponApplied }"
        >
          <Icon
            v-if="!couponStore.isCouponApplied"
            name="fa6-solid:ticket"
            class="-rotate-45 text-brand-primary"
          />
          <Icon v-else name="fa6-solid:circle-check" class="text-green-500"/>
        </span>
        <span class="inline-flex flex-col">
          <span>{{
            couponStore.isCouponApplied
              ? couponStore.activatedCoupon?.coupon.name
              : "Cupom de bônus"
          }}</span>
          <span
            v-if="couponStore.isCouponApplied"
            class="text-xs font-bold text-green-500"
          >
            +{{ showCouponAmount(couponStore.activatedCoupon) }}
          </span>
        </span>
      </label>
      <BaseButton
        v-if="!couponStore.isCouponApplied"
        size="xs"
        class="text-brand-primary"
        @click="
          (couponStore.isCouponInputVisible =
            !couponStore.isCouponInputVisible),
          couponStore.resetCouponInput()
        "
      >
        {{ couponStore.isCouponInputVisible ? "Fechar" : "Possui cupom?" }}
      </BaseButton>

      <BaseButton
        v-else
        size="xs"
        class="gap-2 text-brand-primary"
        @click="couponStore.removeCoupon"
      >
        <Icon name="fa6-solid:trash"/>
        <span>Remover</span>
      </BaseButton>
    </div>

    <transition
      name="fade"
      enter-active-class="transition ease-out duration-300"
      enter-from-class="opacity-0 transform -translate-y-4"
      enter-to-class="opacity-100 transform translate-y-0"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="opacity-100 transform translate-y-0"
      leave-to-class="opacity-0 transform -translate-y-4"
    >
      <div
        v-if="couponStore.isCouponInputVisible"
        class="flex flex-col gap-4"
      >
        <div class="mt-3 flex items-stretch gap-2">
          <BaseInput
            type="text"
            class="flex-1 text-white"
            :variant="couponStore.isCouponApplied && 'success'"
            placeholder="Código do cupom"
            :error="couponStore.couponError"
            v-model="couponStore.couponModel"
            uppercase="true"
          />
          <BaseButton
            :variant="`${
              couponStore.isCouponApplied ? 'secondary-success' : 'secondary'
            }`"
            class="min-h-[52px] min-w-[90px] self-start"
            @click="couponStore.submitCouponCode(couponStore.couponModel)"
          >
            <span
              v-if="!couponStore.isCouponLoading && !couponStore.isCouponApplied"
              >Aplicar</span
            >
            <Icon
              name="fa6-solid:circle-notch"
              class="animate-spin"
              v-if="couponStore.isCouponLoading && !couponStore.isCouponApplied"
            />
            <Icon
              name="fa6-solid:circle-check"
              v-if="couponStore.isCouponApplied"
            />
          </BaseButton>
        </div>

        <!-- Lista de cupons disponíveis -->
        <Disclosure v-if="coupons.length > 0">
          <DisclosureButton>
            <span>
              Utilizar cupons disponíveis
              <Icon
                name="fa6-solid:chevron-down"
                class="text-gray-500"
                aria-hidden="true"
              />
            </span>
          </DisclosureButton>

          <!-- Transição do painel de cupons -->
          <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
          >
            <DisclosurePanel>
              <div
                class="scrollbar-hide max-h-[250px] space-y-4 overflow-y-auto rounded-md bg-primary p-4"
              >
                <div
                  class="flex h-full flex-col overflow-hidden rounded-md border border-gray-700 bg-secondary text-white/70 transition-all duration-300"
                  v-for="coupon in coupons"
                  :key="coupon.id"
                >
                  <header
                    class="flex cursor-pointer justify-between border-b border-gray-700 p-4 transition-all duration-300"
                  >
                    <div class="flex items-center gap-3">
                      <Icon
                        name="fa6-solid:ticket"
                        class="-rotate-45 text-brand-primary"
                      />
                      <h3 class="text-sm">{{ coupon.coupon.name }}</h3>
                    </div>

                    <div class="space-x-2">
                      <BaseBadge
                        v-if="coupon.coupon.amount_type === 1"
                        variant="info"
                      >
                        Bônus
                      </BaseBadge>
                      <BaseBadge
                        v-if="coupon.coupon.amount_type === 2"
                        variant="success"
                      >
                        Crédito
                      </BaseBadge>
                    </div>
                  </header>
                  <div class="flex flex-1 flex-col gap-6 p-4">
                    <div class="flex flex-col gap-1">
                      <div
                        class="flex justify-between whitespace-nowrap text-sm text-gray-500"
                      >
                        <span class="flex items-center gap-2">
                          Valor:
                          <span>{{ showCouponAmount(coupon) }}</span>
                        </span>
                        <BaseButton
                          @click="couponStore.selectCoupon(coupon)"
                          variant="secondary"
                        >
                          Selecionar cupom
                        </BaseButton>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </DisclosurePanel>
          </transition>
        </Disclosure>
      </div>
    </transition>
  </div>
</template>
